import { useContext } from 'react';

import { ArrowDown, MailCheck, SquareArrowOutUpRight } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import {
  AgentInvocationStatus,
  InvocationRuleSetSummary,
  LaneOutcome,
  LaneReasoning,
  QQAgentInvocation,
} from 'types/agents/QuickQuoteAgent';

import { AgentActionsMenu } from '../AgentActionsMenu';
import { AgentFeedback } from './AgentFeedback';
import { BatchLaneList } from './BatchLaneList';
import { KitAvatar } from './KitAvatar';
import {
  LaneReasoningDetails,
  OpenInQuickQuoteHandler,
} from './LaneReasoningDetails';
import { OutcomeChip } from './OutcomeChip';
import { AgentPortalTab, buildAgentPortalUrl } from './agentPortalLinks';
import {
  getLanes,
  getOutcomeDisplay,
  hasDraftedLane,
  isInvocationInProgress,
} from './reasoningHelpers';

type AgentReasoningCardProps = {
  invocation: QQAgentInvocation;
  ruleSet?: InvocationRuleSetSummary;
  agentDisabled: boolean;
  onAgentDisabledChange: (agentId: number, disabled: boolean) => void;
  onOpenInQuickQuote: OpenInQuickQuoteHandler;
  // Present when the Batch Quote section is available below.
  onOpenBatchQuote?: () => void;
};

const OUTCOME_ORDER: LaneOutcome[] = [
  LaneOutcome.Quote,
  LaneOutcome.NeedsReview,
  LaneOutcome.NoBid,
  LaneOutcome.Error,
];

// "4 lanes · 2 drafted · 1 no bid · 1 needs review"
const batchSummary = (lanes: LaneReasoning[]): string => {
  const parts = OUTCOME_ORDER.map((outcome) => {
    const count = lanes.filter((lane) => lane.outcome === outcome).length;
    return count > 0
      ? `${count} ${getOutcomeDisplay(outcome).label.toLowerCase()}`
      : null;
  }).filter(Boolean);
  return [`${lanes.length} lanes`, ...parts].join(' · ');
};

function CardHeader({
  invocation,
  ruleSet,
  outcome,
  agentDisabled,
  onAgentDisabledChange,
}: {
  invocation: QQAgentInvocation;
  ruleSet?: InvocationRuleSetSummary;
  outcome?: LaneOutcome;
  agentDisabled: boolean;
  onAgentDisabledChange: (agentId: number, disabled: boolean) => void;
}) {
  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);

  return (
    <Flex align='center' justify='between' gap='sm' className='w-full'>
      <Flex align='center' gap='sm' className='min-w-0'>
        <KitAvatar />
        <button
          type='button'
          onClick={() =>
            openExternalUrl(buildAgentPortalUrl(invocation.quickQuoteAgentId))
          }
          title='Open this agent in Drumkit Portal'
          className='min-w-0 cursor-pointer truncate text-left text-[13px] font-semibold text-neutral-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
        >
          {ruleSet?.agentName || 'Kit Agent'}
        </button>
      </Flex>
      <Flex align='center' gap='xs' className='shrink-0'>
        {outcome && <OutcomeChip outcome={outcome} />}
        <AgentActionsMenu
          agentId={invocation.quickQuoteAgentId}
          disabled={agentDisabled}
          onDisabledChange={onAgentDisabledChange}
        />
      </Flex>
    </Flex>
  );
}

// "Kit followed your RXO Van Logic rules" with an "Edit rules" deep link to the portal.
function AgentRulesFooter({
  invocation,
  ruleSet,
}: {
  invocation: QQAgentInvocation;
  ruleSet?: InvocationRuleSetSummary;
}) {
  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);

  return (
    <Flex
      align='center'
      justify='between'
      gap='sm'
      className='w-full'
      data-testid='agent-rules-footer'
    >
      <Typography
        variant='body-xs'
        textColor='muted'
        className='min-w-0 truncate text-[11px] leading-normal'
      >
        Kit followed{' '}
        {ruleSet ? (
          <>
            your{' '}
            <Typography
              as='span'
              variant='body-xs'
              className='text-[11px] leading-[inherit] text-neutral-700'
            >
              {ruleSet.agentName}
            </Typography>{' '}
            rules
          </>
        ) : (
          'your rules'
        )}
      </Typography>
      <button
        type='button'
        onClick={() =>
          openExternalUrl(
            buildAgentPortalUrl(
              invocation.quickQuoteAgentId,
              AgentPortalTab.Rules
            )
          )
        }
        title='Change these rules in Drumkit Portal'
        className='inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-[11px] font-medium text-brand-main hover:text-brand-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
      >
        Edit rules
        <SquareArrowOutUpRight className='h-2.5 w-2.5' aria-hidden />
      </button>
    </Flex>
  );
}

// Shown if the card itself fails to render, so the rest of the tab keeps working.
export function AgentReasoningCardFallback() {
  return (
    <Flex
      className='w-full rounded-[4px] border border-neutral-400/70 bg-neutral-50 px-2.5 py-2'
      data-testid='agent-reasoning-card-fallback'
    >
      <Typography variant='body-xs' textColor='lessMuted'>
        Kit&apos;s explanation didn&apos;t load. You can still quote below.
      </Typography>
    </Flex>
  );
}

// Sidebar card for a written-rules agent run: what Kit did and why.
export function AgentReasoningCard({
  invocation,
  ruleSet,
  agentDisabled,
  onAgentDisabledChange,
  onOpenInQuickQuote,
  onOpenBatchQuote,
}: AgentReasoningCardProps) {
  const lanes = getLanes(invocation);
  const isBatch = lanes.length > 1;
  const singleLane = lanes.length === 1 ? lanes[0] : undefined;
  const isInProgress = isInvocationInProgress(invocation);
  const isFailedWithoutLanes =
    !isInProgress &&
    lanes.length === 0 &&
    (invocation.status === AgentInvocationStatus.Failed ||
      Boolean(invocation.error));

  const headerOutcome = (() => {
    if (isFailedWithoutLanes) {
      return LaneOutcome.Error;
    }
    return singleLane?.outcome;
  })();

  const body = (() => {
    if (isInProgress) {
      return (
        <Flex
          align='center'
          className='py-1 text-[12px] text-neutral-600'
          data-testid='agent-in-progress'
        >
          <ButtonLoader className='h-3.5 w-3.5 text-brand-main' />
          Kit is working on this quote…
        </Flex>
      );
    }

    if (isFailedWithoutLanes) {
      return (
        <Flex direction='col' gap='sm' className='w-full'>
          <Typography
            variant='body-xs'
            className='leading-snug text-neutral-700'
          >
            Kit couldn’t finish this one. You can quote it in Quick Quote below.
          </Typography>
          <Button
            type='button'
            variant='secondary'
            size='sm'
            className='h-8 w-fit text-[12px]'
            buttonNamePosthog='qq_agent_written_rules_run_manually'
            onClick={() => onOpenInQuickQuote()}
          >
            Quote it in Quick Quote
          </Button>
        </Flex>
      );
    }

    if (isBatch) {
      return (
        <Flex direction='col' gap='sm' className='w-full'>
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='text-[11.5px] leading-normal'
          >
            {batchSummary(lanes)}
          </Typography>
          {hasDraftedLane(lanes) && (
            <Flex
              align='center'
              gap='xs'
              className='text-[11.5px] text-success-700'
              data-testid='agent-draft-hint'
            >
              <MailCheck
                className='h-3 w-3 shrink-0 text-neutral-500'
                aria-hidden
              />
              <Typography
                as='span'
                variant='body-xs'
                textColor='muted'
                className='text-[11px] leading-normal'
              >
                Draft reply is in your Drumkit folder
              </Typography>
            </Flex>
          )}
          <BatchLaneList
            lanes={lanes}
            onOpenInQuickQuote={onOpenInQuickQuote}
          />
          {onOpenBatchQuote && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-8 w-full gap-1.5 border-neutral-400 text-[12px]'
              buttonNamePosthog='qq_agent_written_rules_open_batch_quote'
              logProperties={{ laneCount: lanes.length }}
              onClick={onOpenBatchQuote}
            >
              Open Batch Quote
              <ArrowDown className='h-3.5 w-3.5' aria-hidden />
            </Button>
          )}
        </Flex>
      );
    }

    if (singleLane) {
      return (
        <Flex direction='col' gap='sm' className='w-full'>
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='-mt-1 break-words text-[11.5px] leading-snug'
          >
            {singleLane.laneLabel}
            {singleLane.miles
              ? ` · ${Math.round(singleLane.miles).toLocaleString('en-US')} mi`
              : ''}
          </Typography>
          <LaneReasoningDetails
            lane={singleLane}
            onOpenInQuickQuote={onOpenInQuickQuote}
          />
        </Flex>
      );
    }

    return (
      <Typography
        variant='body-xs'
        textColor='lessMuted'
        className='leading-normal'
      >
        Kit didn’t find a lane to price in this email.
      </Typography>
    );
  })();

  // Nothing to rate when every lane failed to run.
  const showFeedback =
    !isInProgress && lanes.some((lane) => lane.outcome !== LaneOutcome.Error);

  return (
    <Flex
      direction='col'
      gap='sm'
      className='w-full rounded-[4px] border border-neutral-400/70 bg-neutral-50 px-2.5 py-2'
      data-testid='agent-reasoning-card'
    >
      <CardHeader
        invocation={invocation}
        ruleSet={ruleSet}
        outcome={headerOutcome}
        agentDisabled={agentDisabled}
        onAgentDisabledChange={onAgentDisabledChange}
      />

      {body}

      <div className='w-full border-t border-neutral-200 pt-1.5'>
        <AgentRulesFooter invocation={invocation} ruleSet={ruleSet} />
        {showFeedback && (
          <div className='mt-1'>
            <AgentFeedback invocationId={invocation.id} />
          </div>
        )}
      </div>
    </Flex>
  );
}
